/**
 * Location chat panel.
 *
 * A right-hand drawer with no overlay, so the hierarchy stays visible and editable while the user
 * talks to the agent - the proposed changes are previewed in that tree, which only works if the tree
 * is on screen.
 *
 * Purely presentational: every piece of state lives in LocationManagementPage, because the page
 * needs the pending proposal to build the preview tree.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  Group,
  List,
  Loader,
  Paper,
  Stack,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { IconArrowUp, IconCheck, IconCopy, IconSparkles } from '@tabler/icons-react';
import type { LocationChatMessage } from '../../../api/schedulingApis';
import type { ChatApplyPlan } from '../logic/locationChatApply';
import { shellDrawerClassNames } from '../../../app/shellDrawerClassNames';

/** An ordinary panel surface, matching the details panel on the other side of the tree. */
const PANEL_SURFACE = 'var(--mantine-color-body)';

/** Preferred width; the page narrows it when the viewport cannot spare this much. */
export const LOCATION_CHAT_DRAWER_WIDTH = 480;

/**
 * Below this there is no room to sit beside the workspace, so the drawer overlays it as before -
 * insetting would leave a uselessly thin tree.
 */
export const LOCATION_CHAT_INSET_MIN_VIEWPORT = 1024;

/** Widest the drawer may get, and the narrowest it is still usable at. */
export function resolveChatWidth(viewportWidth: number): number {
  if (viewportWidth <= 0) return LOCATION_CHAT_DRAWER_WIDTH;
  return Math.max(300, Math.min(LOCATION_CHAT_DRAWER_WIDTH, Math.round(viewportWidth * 0.4)));
}

export interface LocationChatDrawerProps {
  opened: boolean;
  onClose: () => void;
  /** Conversation as returned by the server, rendered verbatim. */
  history: LocationChatMessage[];
  /** The turn in flight, shown optimistically before the server echoes it back. */
  pendingUserText: string | null;
  sending: boolean;
  error: string | null;
  /** Planned result of the pending proposal, or null when nothing is proposed. */
  plan: ChatApplyPlan | null;
  /** Summaries of changes the server applied itself. Empty while proposals go into the draft. */
  appliedActions: string[];
  /** False when the user may not change locations at all - they can still ask questions. */
  canMutate: boolean;
  /** Resolved by the page so the drawer and the page inset stay in step. */
  width: number;
  /** First name for the opening greeting; omitted when the session does not carry one. */
  greetingName?: string;
  /** Opening suggestions, built by the page from the locations actually in this project. */
  suggestions: string[];
  onSend: (text: string) => void;
  onConfirm: () => void;
  onDiscard: () => void;
}

/**
 * Render an assistant message as ordinary page content.
 *
 * The agent composes its change summaries as hyphen bullets, which read as a wall of text when
 * dumped into a paragraph. Consecutive bullet lines become a real list; everything else stays
 * paragraphs. A trailing "Confirm?" is dropped - the buttons below ask that question.
 */
function MessageBody({ content }: { content: string }) {
  const lines = content.split('\n').map((l) => l.trimEnd());
  const blocks: Array<{ kind: 'text'; value: string } | { kind: 'list'; items: string[] }> = [];

  for (const line of lines) {
    const bullet = /^[-•]\s+(.*)$/.exec(line.trim());
    const last = blocks[blocks.length - 1];
    if (bullet) {
      if (last?.kind === 'list') last.items.push(bullet[1]);
      else blocks.push({ kind: 'list', items: [bullet[1]] });
    } else if (line.trim() && line.trim() !== 'Confirm?') {
      if (last?.kind === 'text') last.value += `\n${line}`;
      else blocks.push({ kind: 'text', value: line });
    }
  }

  return (
    <Stack gap={6}>
      {blocks.map((block, i) =>
        block.kind === 'list' ? (
          <List key={i} size="sm" spacing={2} withPadding>
            {block.items.map((item, n) => (
              <List.Item key={n}>{item}</List.Item>
            ))}
          </List>
        ) : (
          <Text key={i} size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {block.value}
          </Text>
        ),
      )}
    </Stack>
  );
}

/**
 * Copy inside the click that asked for it.
 *
 * Order matters. `execCommand` only works while the click's user activation is still live, and
 * awaiting the async clipboard spends it - so a fallback placed after that await silently refuses
 * and the button appears to do nothing. The async API is therefore used only when it can actually
 * succeed (present, and the document focused); otherwise the synchronous path runs first.
 */
function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText && document.hasFocus()) {
    return navigator.clipboard.writeText(text).then(
      () => true,
      () => copyViaSelection(text),
    );
  }
  return Promise.resolve(copyViaSelection(text));
}

function copyViaSelection(text: string): boolean {
  try {
    const scratch = document.createElement('textarea');
    scratch.value = text;
    scratch.setAttribute('readonly', '');
    scratch.style.position = 'fixed';
    scratch.style.opacity = '0';
    document.body.appendChild(scratch);
    scratch.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(scratch);
    return copied;
  } catch {
    return false;
  }
}

function CopyMessageButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    void copyText(text).then((ok) => {
      if (!ok) return;
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);

  return (
    <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
      <ActionIcon variant="subtle" color="gray" size="sm" onClick={copy} aria-label={label}>
        {copied ? <IconCheck size={14} stroke={1.75} /> : <IconCopy size={14} stroke={1.75} />}
      </ActionIcon>
    </Tooltip>
  );
}

/**
 * The user's own turn: a compact bubble, aligned right.
 *
 * The asymmetry is the whole point - a short request reads as an aside, so the eye goes to the
 * answer. Nothing labels the two sides; alignment does it.
 */
function UserTurn({ content }: { content: string }) {
  return (
    <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <Box
        style={{
          maxWidth: '85%',
          padding: '8px 12px',
          borderRadius: 16,
          backgroundColor: 'var(--mantine-color-default-hover)',
        }}
      >
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {content}
        </Text>
      </Box>
      <Group gap={2} mt={2}>
        <CopyMessageButton text={content} label="Copy query" />
      </Group>
    </Box>
  );
}

/** The answer: page content at full width, with the one action that does something real. */
function AssistantTurn({ content }: { content: string }) {
  return (
    <Box>
      <MessageBody content={content} />
      <Group gap={2} mt={2}>
        <CopyMessageButton text={content} label="Copy answer" />
      </Group>
    </Box>
  );
}

function ProposalCard({
  plan,
  onConfirm,
  onDiscard,
}: {
  plan: ChatApplyPlan;
  onConfirm: () => void;
  onDiscard: () => void;
}) {
  // Locations touched, not ops: a rename+move is two ops but one affected location, and deleting a
  // branch is one op but several.
  const affected = plan.marks.size;
  return (
    <Paper withBorder radius="sm" p="sm">
      <Group justify="space-between" wrap="nowrap" mb={6}>
        <Text size="sm" fw={600}>
          Proposed changes
        </Text>
        <Badge size="sm" variant="light">
          {affected === 1 ? '1 location' : `${affected} locations`}
        </Badge>
      </Group>
      <Text size="xs" c="dimmed">
        Highlighted in the hierarchy. Nothing is saved until you press Save.
      </Text>

      {plan.skipped.length > 0 && (
        <>
          <Divider my={8} />
          <Text size="xs" fw={600} c="dimmed" mb={4}>
            {plan.skipped.length === 1
              ? '1 change no longer applies'
              : `${plan.skipped.length} changes no longer apply`}
          </Text>
          <List size="xs" spacing={2} withPadding c="dimmed">
            {plan.skipped.map((s, i) => (
              <List.Item key={i}>
                {s.summary} — {s.reason}
              </List.Item>
            ))}
          </List>
        </>
      )}

      <Group gap={8} justify="flex-end" mt={12}>
        <Button variant="default" size="xs" onClick={onDiscard}>
          Discard
        </Button>
        <Button size="xs" onClick={onConfirm} disabled={plan.ops.length === 0}>
          Confirm
        </Button>
      </Group>
    </Paper>
  );
}

export function LocationChatDrawer({
  opened,
  onClose,
  history,
  pendingUserText,
  sending,
  error,
  plan,
  appliedActions,
  canMutate,
  width,
  greetingName,
  suggestions,
  onSend,
  onConfirm,
  onDiscard,
}: LocationChatDrawerProps) {
  const [value, setValue] = useState('');
  const endRef = useRef<HTMLDivElement | null>(null);

  // Recomputed per render rather than memoised: it is one Date read, and pinning it would leave a
  // panel opened before noon still saying "morning" hours later.
  const hour = new Date().getHours();
  const timeOfDay = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const greeting = greetingName ? `${timeOfDay}, ${greetingName}` : timeOfDay;

  useEffect(() => {
    // Instant, not smooth - keeps prefers-reduced-motion happy without a media query.
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [history.length, sending, plan]);

  const submit = useCallback(() => {
    const text = value.trim();
    if (!text || sending) return;
    setValue('');
    onSend(text);
  }, [value, sending, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit],
  );

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      // The page clears tree selection on any outside pointerdown; this class is on its whitelist.
      classNames={{ root: 'location-chat-drawer-root', ...shellDrawerClassNames }}
      title={
        <Group gap={8}>
          <IconSparkles size={16} stroke={1.75} />
          <Text size="sm" fw={600}>
            Location assistant
          </Text>
        </Group>
      }
      position="right"
      size={width}
      withOverlay={false}
      trapFocus={false}
      lockScroll={false}
      closeOnClickOutside={false}
      transitionProps={{ duration: 200 }}
      styles={{
        content: {
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: PANEL_SURFACE,
          // No drop shadow: the panel sits beside the workspace rather than floating over it, so
          // it should read as another pane of the page - the same plain divider the details panel
          // on the other side of the tree uses.
          boxShadow: 'none',
          borderLeft: '1px solid var(--mantine-color-default-border)',
        },
        header: {
          flexShrink: 0,
          backgroundColor: PANEL_SURFACE,
          borderBottom: '1px solid var(--mantine-color-default-border)',
          minHeight: 48,
        },
        title: { flex: 1, marginRight: 8 },
        body: {
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: PANEL_SURFACE,
          padding: 0,
          overflow: 'hidden',
        },
      }}
    >
      <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 'var(--mantine-spacing-md)' }}>
        <Stack gap={14}>
          {!canMutate && (
            <Text size="xs" c="dimmed">
              You can ask questions, but you don&apos;t have permission to change locations.
            </Text>
          )}

          {history.length === 0 && !pendingUserText && (
            <Stack gap="lg" pt={36}>
              <Stack gap={8} align="center">
                <IconSparkles size={26} stroke={1.4} />
                <Text size="lg" fw={500} ta="center">
                  {greeting}
                </Text>
                <Text size="sm" c="dimmed" ta="center" maw={300}>
                  Ask about this project&apos;s locations, describe a change, or have the hierarchy
                  built from the schedule tasks you have already imported.
                </Text>
              </Stack>
              <Stack gap={6}>
                {suggestions.map((prompt) => (
                  <UnstyledButton
                    key={prompt}
                    onClick={() => setValue(prompt)}
                    style={{
                      border: '1px solid var(--mantine-color-default-border)',
                      borderRadius: 12,
                      padding: '8px 12px',
                    }}
                  >
                    <Text size="sm">{prompt}</Text>
                  </UnstyledButton>
                ))}
              </Stack>
            </Stack>
          )}

          {history.map((message, i) =>
            message.role === 'user' ? (
              <UserTurn key={i} content={message.content} />
            ) : (
              <AssistantTurn key={i} content={message.content} />
            ),
          )}

          {pendingUserText && <UserTurn content={pendingUserText} />}

          {sending && (
            <Group gap={8}>
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                Thinking…
              </Text>
            </Group>
          )}

          {plan && <ProposalCard plan={plan} onConfirm={onConfirm} onDiscard={onDiscard} />}

          {appliedActions.length > 0 && (
            <Stack gap={2}>
              {appliedActions.map((applied, i) => (
                <Text key={i} size="xs" c="dimmed">
                  {applied}
                </Text>
              ))}
            </Stack>
          )}

          {error && (
            <Text size="sm" c="red">
              {error}
            </Text>
          )}

          <div ref={endRef} />
        </Stack>
      </Box>

      <Box style={{ flexShrink: 0, padding: 'var(--mantine-spacing-sm)' }}>
        {/* Field and its controls share one rounded card, so the composer reads as a single
            surface rather than an input with a button parked next to it. */}
        <Box
          style={{
            border: '1px solid var(--mantine-color-default-border)',
            borderRadius: 18,
            backgroundColor: 'var(--mantine-color-body)',
            padding: '6px 8px 8px',
          }}
        >
          <Textarea
            variant="unstyled"
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            placeholder="How can I help you today?"
            autosize
            minRows={2}
            maxRows={8}
            disabled={sending}
            styles={{ input: { padding: '2px 6px' } }}
          />
          <Group justify="space-between" align="center" wrap="nowrap" mt={2}>
            <Text size="xs" c="dimmed" pl={6}>
              Enter to send
            </Text>
            <ActionIcon
              radius="xl"
              size={34}
              onClick={submit}
              disabled={sending || !value.trim()}
              aria-label="Send"
            >
              <IconArrowUp size={18} stroke={2} />
            </ActionIcon>
          </Group>
        </Box>
      </Box>
    </Drawer>
  );
}
